/**
 * LifecycleManager - Component lifecycle management
 *
 * Manages component lifecycle (init, update, destroy) for all registered
 * components. Provides centralized control over component state changes.
 *
 * Based on design document section 3.2 (Lifecycle Manager).
 */

import type {
  LifecycleManager as ILifecycleManager,
  ComponentRegistry,
} from '../types/index.js';

/**
 * Configuration for LifecycleManager
 */
interface LifecycleManagerConfig {
  /** Enable debug logging */
  debug: boolean;
  /** Continue processing even if individual components fail */
  continueOnError: boolean;
  /** Maximum time to wait for component operations (ms) */
  timeout: number;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: LifecycleManagerConfig = {
  debug: false,
  continueOnError: true,
  timeout: 5000,
};

/**
 * LifecycleManager implementation
 */
export class LifecycleManager implements ILifecycleManager {
  private config: LifecycleManagerConfig;
  private registry: ComponentRegistry;

  constructor(registry: ComponentRegistry, config: Partial<LifecycleManagerConfig> = {}) {
    this.registry = registry;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Initialize all components
   */
  initAll(): void {
    try {
      this.log('Initializing all components...');
      
      // Delegate to registry's scanAndInitialize method
      this.registry.scanAndInitialize();
      
      const instances = this.registry.getInstances();
      this.log(`Initialized ${instances.length} components`);
    } catch (error) {
      this.handleError('Error during component initialization', error);
      
      if (!this.config.continueOnError) {
        throw error;
      }
    }
  }

  /**
   * Update all components with new data
   */
  updateAll(data: any): void {
    try {
      this.log('Updating all components with new data...');
      
      const instances = this.registry.getInstances();
      let successCount = 0;
      let errorCount = 0;

      instances.forEach((instance) => {
        try {
          this.updateComponent(instance.id, data);
          successCount++;
        } catch (error) {
          errorCount++;
          this.handleError(`Error updating component ${instance.id}`, error);
          
          if (!this.config.continueOnError) {
            throw error;
          }
        }
      });

      this.log(`Updated ${successCount} components successfully, ${errorCount} failed`);
    } catch (error) {
      this.handleError('Error during bulk component update', error);
      
      if (!this.config.continueOnError) {
        throw error;
      }
    }
  }

  /**
   * Destroy all components
   */
  destroyAll(): void {
    try {
      this.log('Destroying all components...');
      
      const instances = this.registry.getInstances();
      let successCount = 0;
      let errorCount = 0;

      // Create a copy of instance IDs to avoid modification during iteration
      const instanceIds = instances.map(instance => instance.id);

      instanceIds.forEach((id) => {
        try {
          this.registry.destroyInstance(id);
          successCount++;
        } catch (error) {
          errorCount++;
          this.handleError(`Error destroying component ${id}`, error);
          
          if (!this.config.continueOnError) {
            throw error;
          }
        }
      });

      this.log(`Destroyed ${successCount} components successfully, ${errorCount} failed`);
    } catch (error) {
      this.handleError('Error during bulk component destruction', error);
      
      if (!this.config.continueOnError) {
        throw error;
      }
    }
  }

  /**
   * Update specific component
   */
  updateComponent(id: string, data: any): void {
    try {
      const instance = this.registry.getInstance(id);
      
      if (!instance) {
        this.log(`Component instance not found: ${id}`);
        return;
      }

      // Validate that component has update method
      if (typeof instance.update !== 'function') {
        throw new Error(`Component ${id} does not have an update method`);
      }

      this.log(`Updating component: ${id} (${instance.type})`);
      
      // Call component's update method
      instance.update(data);
      
      this.log(`Successfully updated component: ${id}`);
    } catch (error) {
      this.handleError(`Error updating component ${id}`, error);
      throw error;
    }
  }

  /**
   * Update components of specific type
   */
  updateComponentsByType(type: string, data: any): void {
    try {
      this.log(`Updating all components of type: ${type}`);
      
      const instances = this.registry.getInstances(type);
      let successCount = 0;
      let errorCount = 0;

      instances.forEach((instance) => {
        try {
          this.updateComponent(instance.id, data);
          successCount++;
        } catch (error) {
          errorCount++;
          
          if (!this.config.continueOnError) {
            throw error;
          }
        }
      });

      this.log(`Updated ${successCount} components of type ${type}, ${errorCount} failed`);
    } catch (error) {
      this.handleError(`Error updating components of type ${type}`, error);
      
      if (!this.config.continueOnError) {
        throw error;
      }
    }
  }

  /**
   * Reinitialize all components (destroy and recreate)
   */
  reinitializeAll(): void {
    try {
      this.log('Reinitializing all components...');
      
      // Destroy all existing components
      this.destroyAll();
      
      // Initialize components again
      this.initAll();
      
      this.log('Component reinitialization complete');
    } catch (error) {
      this.handleError('Error during component reinitialization', error);
      
      if (!this.config.continueOnError) {
        throw error;
      }
    }
  }

  /**
   * Get lifecycle statistics
   */
  getStats(): {
    totalComponents: number;
    componentsByType: Record<string, number>;
    registeredTypes: string[];
  } {
    const instances = this.registry.getInstances();
    const componentsByType: Record<string, number> = {};

    // Count components by type
    instances.forEach((instance) => {
      componentsByType[instance.type] = (componentsByType[instance.type] || 0) + 1;
    });

    return {
      totalComponents: instances.length,
      componentsByType,
      registeredTypes: this.registry.getRegisteredTypes(),
    };
  }

  /**
   * Check if component exists and is healthy
   */
  isComponentHealthy(id: string): boolean {
    try {
      const instance = this.registry.getInstance(id);
      
      if (!instance) {
        return false;
      }

      // Check if element is still in DOM
      if (!document.contains(instance.element)) {
        this.log(`Component ${id} element is no longer in DOM`);
        return false;
      }

      // Check if component has required methods
      if (typeof instance.update !== 'function' || typeof instance.destroy !== 'function') {
        this.log(`Component ${id} is missing required methods`);
        return false;
      }

      return true;
    } catch (error) {
      this.handleError(`Error checking component health for ${id}`, error);
      return false;
    }
  }

  /**
   * Perform health check on all components
   */
  performHealthCheck(): {
    healthy: string[];
    unhealthy: string[];
    total: number;
  } {
    const instances = this.registry.getInstances();
    const healthy: string[] = [];
    const unhealthy: string[] = [];

    instances.forEach((instance) => {
      if (this.isComponentHealthy(instance.id)) {
        healthy.push(instance.id);
      } else {
        unhealthy.push(instance.id);
      }
    });

    this.log(`Health check complete: ${healthy.length} healthy, ${unhealthy.length} unhealthy`);

    return {
      healthy,
      unhealthy,
      total: instances.length,
    };
  }

  /**
   * Clean up unhealthy components
   */
  cleanupUnhealthyComponents(): void {
    const healthCheck = this.performHealthCheck();
    
    if (healthCheck.unhealthy.length === 0) {
      this.log('No unhealthy components found');
      return;
    }

    this.log(`Cleaning up ${healthCheck.unhealthy.length} unhealthy components`);

    healthCheck.unhealthy.forEach((id) => {
      try {
        this.registry.destroyInstance(id);
      } catch (error) {
        this.handleError(`Error cleaning up unhealthy component ${id}`, error);
      }
    });
  }

  /**
   * Handle errors and log them
   */
  private handleError(message: string, error: unknown): void {
    console.error(`LifecycleManager: ${message}`, error);

    // Could send error message to parent via MessageHandler if available
    // For now, just log to console
  }

  /**
   * Debug logging
   */
  private log(message: string, ...args: unknown[]): void {
    if (this.config.debug) {
      // eslint-disable-next-line no-console
      console.log(`[LifecycleManager] ${message}`, ...args);
    }
  }

  /**
   * Enable debug mode
   */
  enableDebug(): void {
    this.config.debug = true;
    this.log('Debug mode enabled');
  }

  /**
   * Disable debug mode
   */
  disableDebug(): void {
    this.config.debug = false;
  }

  /**
   * Get current configuration
   */
  getConfig(): LifecycleManagerConfig {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<LifecycleManagerConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.log('Configuration updated:', this.config);
  }
}